import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { updateSegmentOperation } from '@/api/business/operation'
import { operationSegmentKeys } from '@/queries/useOperationQuery'
import Button from '@/components/Button'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    
} from '@/components/ui/form'
 
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod' 
import { useCompaniesQuery } from '@/queries/useCompaniesQuery'  
import { useEffect } from 'react'
import { SegmentOperation } from '@/types/Operation'

interface EditDialogProps {
    Segment: SegmentOperation
    isOpen: boolean
    onClose: () => void
}

const EditDialog = ({ Segment, isOpen, onClose }: EditDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 
    console.log(Segment)

    const { data: dataCompanies } = useCompaniesQuery() 

    const schema = z.object({ 
        SK_EMPRESA_ORIGEM: z.string(),
        SK_EMPRESA_DESTINO: z.string(),
        DD_TEMPO_OPERACAO: z.string(), 
    })

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: { 
            SK_EMPRESA_ORIGEM: Segment.SK_EMPRESA_ORIGEM || '',
            SK_EMPRESA_DESTINO: Segment.SK_EMPRESA_DESTINO || '',
            DD_TEMPO_OPERACAO: Segment.DD_TEMPO_OPERACAO || '' 
        },
    })
    

    useEffect(() => {
        form.reset({
            SK_EMPRESA_ORIGEM: Segment.SK_EMPRESA_ORIGEM || '',
            SK_EMPRESA_DESTINO: Segment.SK_EMPRESA_DESTINO || '',
            DD_TEMPO_OPERACAO: Segment.DD_TEMPO_OPERACAO || '' 
        })
    }, [Segment])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: updateSegmentOperation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: operationSegmentKeys.details() })
            onClose()
            form.reset()
            toast({
                title: 'Trecho criado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar o trecho. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar trecho',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => { 
        mutate({
            ...data,
            SK_OPERACAO: Segment.SK_OPERACAO
        })
    }

    const handleChange = (e) => {
        let value = e.target.value;

        // Remove tudo que não for número ou dois pontos
        value = value.replace(/[^0-9:]/g, '');

        // Adiciona ":" automaticamente após dois dígitos
        if (value.length > 2 && value.indexOf(':') === -1) {
        value = value.substring(0, 2) + ':' + value.substring(2);
        }

        // Validação do formato HH:mm
        const parts = value.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);

        // Limita horas a 99
        if (parts[0].length > 2 || hours > 99) {
        value = '99';
        }

        // Limita minutos a 59
        if (parts[1] && (parts[1].length > 2 || minutes > 59)) {
        value = parts[0] + ':59';
        }

        return value;
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[400px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Adicionar novo Trecho</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    > 
                        <FormField
                            name="SK_EMPRESA_ORIGEM"
                            control={form.control}
                            render={({ field }) => (
                                    <FormItem>
                                    <FormLabel>Fornecedor - Origem</FormLabel>
                                    <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()}>
                                        <FormControl>
                                            <SelectTrigger>
                                            <SelectValue placeholder="" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {dataCompanies?.map((companies) => (
                                                <SelectItem key={companies.SK_EMPRESA} value={companies.SK_EMPRESA}>{companies.DS_ABREVIATURA}</SelectItem>
                                            ))} 
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />  
                         
                        <FormField
                            name="SK_EMPRESA_DESTINO"
                            control={form.control}
                            render={({ field }) => (
                                    <FormItem>
                                    <FormLabel>Cliente - Destino</FormLabel>
                                    <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()}>
                                        <FormControl>
                                            <SelectTrigger>
                                            <SelectValue placeholder="" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {dataCompanies?.map((companies) => (
                                                <SelectItem key={companies.SK_EMPRESA} value={companies.SK_EMPRESA}>{companies.DS_ABREVIATURA}</SelectItem>
                                            ))} 
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />  
                        <FormField
                            name="DD_TEMPO_OPERACAO"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Tempo de Operação</FormLabel>
                                    <FormControl>
                                         <input {...field} type='text' onChange={(e) => { 
                                                field.onChange(handleChange(e))
                                            }} placeholder="00:00" maxLength={6} className='flex h-9 w-full text-foreground rounded-md bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />  
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditDialog
